/**
 * Retell Management Hub
 * Accessed only after unlocking with master password via ManagementSetup
 * Shows Retell AI configuration, call logs, and version history for all tenants
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { apiUrl } from './utils/api';

function SettingsRetellManagement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('status');
  const [retellStatus, setRetellStatus] = useState(null);
  const [calls, setCalls] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [promptValue, setPromptValue] = useState('');
  const [promptSource, setPromptSource] = useState('localStorage');
  const [promptNotice, setPromptNotice] = useState('');
  const [promptIsNewTemplate, setPromptIsNewTemplate] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [agentId, setAgentId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const managementHeaders = (extra = {}) => ({
    ...extra,
    'x-management-token': localStorage.getItem('management_token') || '',
  });

  const getStarterPrompt = () => `# DoorBase Retell Agent Prompt

## Company Context
You are the DoorBase AI assistant for this company account.

## Primary Role
Help callers with door enquiries, job details, bookings, updates, and follow-up actions.

## Operating Rules
- Ask clear questions before confirming details.
- Keep answers practical and specific.
- Record names, phone numbers, addresses, job references, and urgency where available.
- Escalate anything uncertain to the DoorBase team.

## Account Notes
Add the account-specific instructions for this Retell agent here.
`;

  const setMissingPromptState = () => {
    const currentAgent = agentId || retellStatus?.agent_id || 'selected agent';
    setPromptValue('');
    setPromptSource(`New template for ${currentAgent}`);
    setPromptIsNewTemplate(true);
    setPromptNotice('No GitHub prompt template exists for this Retell agent yet. Create one here, then Save Prompt will create the current template, add a version file, save DoorBase, and push it to Retell.');
  };

  const insertStarterPrompt = () => {
    setPromptValue(getStarterPrompt());
    setPromptSource('Starter template');
    setPromptIsNewTemplate(true);
    setPromptNotice('Starter template inserted. Edit the account instructions, then Save Prompt to create the GitHub current/version files and update Retell.');
    setActiveTab('agent');
  };

  useEffect(() => {
    if (!localStorage.getItem('management_token')) {
      navigate('/settings/management-setup');
      return;
    }
    loadData();
    loadPrompt();
  }, []);

  // Fetch prompt: GitHub raw (canonical) → DoorBase DB/file → localStorage fallback
  const loadPrompt = async () => {
    setLoadingPrompt(true);
    setPromptIsNewTemplate(false);
    setPromptNotice('Loading current prompt template...');
    try {
      // 1. GitHub raw — canonical template source (fetched by agent_id from company record)
      const ghRes = await fetch(apiUrl(`/api/retell/admin/prompt-github?company_id=${selectedCompany || 1}`), {
        headers: managementHeaders()
      });
      if (ghRes.ok) {
        const data = await ghRes.json();
        if (data.content) {
          setPromptValue(data.content);
          setPromptSource(data.source || 'GitHub current');
          setPromptNotice(`Loaded current template from ${data.source || 'GitHub current'}${data.file ? ` (${data.file})` : ''}.`);
          setPromptIsNewTemplate(false);
          localStorage.setItem('retell_prompt_value', data.content);
          localStorage.setItem('retell_prompt_source', data.source || 'GitHub current');
          setLoadingPrompt(false);
          return;
        }
      }
    } catch (err) {
      console.error('Error loading prompt from GitHub:', err);
    }

    try {
      // 2. DoorBase source (DB or file)
      const res = await fetch(apiUrl(`/api/retell/admin/prompt-source?company_id=${selectedCompany || 1}`), {
        headers: managementHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.content) {
          setPromptValue(data.content);
          setPromptSource(data.source || 'DoorBase');
          setPromptNotice(`Loaded fallback prompt from ${data.source || 'DoorBase'}. Save Prompt will create/update GitHub current and version history for this Retell agent.`);
          setPromptIsNewTemplate(false);
          localStorage.setItem('retell_prompt_value', data.content);
          localStorage.setItem('retell_prompt_source', data.source || 'DoorBase');
          setLoadingPrompt(false);
          return;
        }
      }
    } catch (err) {
      console.error('Error loading prompt from DoorBase:', err);
    }

    // 3. Final fallback: browser cache
    const savedPrompt = localStorage.getItem('retell_prompt_value');
    if (savedPrompt) {
      setPromptValue(savedPrompt);
      setPromptSource(localStorage.getItem('retell_prompt_source') || 'Restored from browser');
      setPromptNotice('Loaded the browser-saved prompt because no server template was found. Save Prompt will write this to DoorBase, GitHub, and Retell.');
      setPromptIsNewTemplate(true);
    } else {
      setMissingPromptState();
      setActiveTab('agent');
    }
    setLoadingPrompt(false);
  };

  // Check session validity
  useEffect(() => {
    const expires = localStorage.getItem('management_expires');
    if (expires && Date.now() > parseInt(expires)) {
      localStorage.removeItem('management_token');
      localStorage.removeItem('management_expires');
      navigate('/settings/management-setup');
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadStatus(),
      loadVersions()
    ]);
    setLoading(false);
  };

  const loadStatus = async () => {
    try {
      // Get Retell status for selected company
      const res = await fetch(apiUrl(`/api/retell/admin/status?company_id=${selectedCompany || 1}`), {
        headers: managementHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setRetellStatus(data);
        setAgentId(data.agent_id || '');
        setApiKey('');
        setPhoneNumber(data.phone_number || '');
        setWebhookUrl(data.webhook_url || '');
        setWebhookSecret('');
      }
    } catch (err) {
      console.error('Error loading Retell status:', err);
    }
  };

  const loadVersions = async () => {
    try {
      const res = await fetch(apiUrl(`/api/retell/admin/prompt-versions?company_id=${selectedCompany || 1}&limit=20`), {
        headers: managementHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
      }
    } catch (err) {
      console.error('Error loading prompt versions:', err);
    }
  };

  const handleRestoreVersion = async (versionId) => {
    if (!window.confirm('Restore this version into the editor? Current unsaved changes will be lost.')) return;
    try {
      const res = await fetch(apiUrl(`/api/retell/admin/prompt-version/${versionId}`), {
        headers: managementHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.version?.content) {
          setPromptValue(data.version.content);
          setPromptSource(`Restored save #${data.version.id} (prompt v${data.version.version || 'n/a'})`);
          setActiveTab('agent');
          alert('Version restored into editor. Review and save to apply.');
        }
      }
    } catch (err) {
      alert('Failed to restore version: ' + err.message);
    }
  };

  const loadCalls = async (companyId) => {
    if (!companyId) return;
    try {
      const res = await fetch(apiUrl(`/api/retell/admin/calls?company_id=${companyId}&limit=50`), {
        headers: managementHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCalls(data.calls || []);
      }
    } catch (err) {
      console.error('Error loading calls:', err);
    }
  };

  const handleSavePrompt = async () => {
    if (!selectedCompany || !promptValue) return;
    setSaving(true);
    try {
      const res = await fetch(apiUrl('/api/retell/admin/prompt-source'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-management-token': localStorage.getItem('management_token') || ''
        },
        body: JSON.stringify({ company_id: parseInt(selectedCompany), content: promptValue })
      });
      const data = await res.json();
      if (!data.success) {
        alert('Save failed: ' + (data.error || 'Unknown error'));
      } else if (!data.savedLocally && !data.savedToDb) {
        alert('Save failed: ' + (data.dbError || 'Check file write and database permissions'));
      } else if (data.savedLocally || data.savedToDb) {
        alert(data.message || 'Saved.');
        setSaved(true);
        setPromptSource(data.github?.savedCurrent ? 'GitHub current' : data.savedToDb ? 'DoorBase DB' : 'Local source');
        setPromptNotice('');
        setPromptIsNewTemplate(false);
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Failed to save prompt: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWebhook = async () => {
    if (!selectedCompany || !webhookUrl) return;
    setSaving(true);
    try {
      await fetch(apiUrl('/api/retell/admin/webhook'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-management-token': localStorage.getItem('management_token') || ''
        },
        body: JSON.stringify({ company_id: parseInt(selectedCompany), webhook_url: webhookUrl })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Failed to save webhook URL');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWebhookSecret = async () => {
    if (!selectedCompany || !webhookSecret) return;
    setSaving(true);
    try {
      await fetch(apiUrl('/api/retell/admin/webhook-secret'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-management-token': localStorage.getItem('management_token') || ''
        },
        body: JSON.stringify({ company_id: parseInt(selectedCompany), webhook_secret: webhookSecret })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Failed to save webhook secret');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCredentials = async () => {
    const targetCompany = selectedCompany || '1';
    setSelectedCompany(targetCompany);
    setSaving(true);
    try {
      const res = await fetch(apiUrl('/api/retell/admin/setup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-management-token': localStorage.getItem('management_token') || ''
        },
        body: JSON.stringify({
          company_id: parseInt(targetCompany),
          retell_agent_id: agentId || undefined,
          retell_api_key: apiKey || undefined,
          retell_phone_number: phoneNumber || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        setSaved(true);
        setRetellStatus(prev => ({ ...prev, configured: true, agent_id: agentId, has_api_key: !!apiKey, api_key: apiKey, phone_number: phoneNumber }));
        setTimeout(() => setSaved(false), 3000);
      } else {
        alert('Save failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to save credentials: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLock = async () => {
    await fetch(apiUrl('/api/management/lock'), {
      method: 'POST',
      headers: managementHeaders()
    });
    localStorage.removeItem('management_token');
    localStorage.removeItem('management_expires');
    navigate('/settings/management-setup');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'calls' && selectedCompany) loadCalls(selectedCompany);
    // Agent tab: do NOT auto-load prompt; user must click Load
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Retell AI Management" subtitle="Loading..." sticky />
        <main className="p-8 text-center text-gray-500">Loading management data...</main>
      </div>
    );
  }

  const tabs = [
    { id: 'status', label: 'Status', icon: '📊' },
    { id: 'agent', label: 'Agent Config', icon: '🤖' },
    { id: 'webhook', label: 'Webhook', icon: '🔗' },
    { id: 'calls', label: 'Call Log', icon: '📞' },
    { id: 'versions', label: 'Version History', icon: '📜' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Retell AI Management"
        subtitle="Platform-wide Retell AI configuration"
        backTo="/settings/management-hub"
        backLabel="Management Hub"
        sticky
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Token Status Bar */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔓</span>
            <div>
              <p className="text-sm font-medium text-blue-900">Management Unlocked</p>
              <p className="text-xs text-blue-700">
                Expires: {new Date(parseInt(localStorage.getItem('management_expires') || 0)).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={handleLock}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            🔒 Lock
          </button>
        </div>

        {/* Retell Management Hub */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Retell AI Management</h1>
          <p className="text-gray-600">Configure and monitor the Retell AI voice agent for DoorBase.</p>
        </div>

        {/* Company Info */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Managing Retell for:</label>
          <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900 font-medium">
            DoorBase (Company 1)
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* STATUS TAB */}
            {activeTab === 'status' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium mb-4">Retell Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID</label>
                    <input
                      type="text"
                      value={agentId}
                      onChange={(e) => setAgentId(e.target.value)}
                      placeholder="agent_xxxxx"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={retellStatus?.has_api_key ? 'A key is already saved. Enter a new one to replace it.' : 'key_xxxxx'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
                      >
                        👁
                      </button>
                    </div>
                    {showApiKey && apiKey && (
                      <p className="mt-1 text-xs text-gray-500 font-mono">{apiKey}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+44xxxx"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <button
                    onClick={handleSaveCredentials}
                    disabled={saving}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
                  >
                    {saving ? 'Saving...' : '💾 Save Credentials'}
                  </button>
                  {saved && <span className="text-green-600 font-medium">✅ Saved!</span>}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Current DoorBase Status</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Configured</p>
                      <p className={`text-sm font-medium ${retellStatus?.configured ? 'text-green-600' : 'text-red-600'}`}>
                        {retellStatus?.configured ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Agent ID Set</p>
                      <p className="text-sm font-medium">{retellStatus?.agent_id ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">API Key Set</p>
                      <p className="text-sm font-medium">{retellStatus?.has_api_key ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-mono">{retellStatus?.phone_number || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AGENT CONFIG TAB */}
            {activeTab === 'agent' && (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Agent Prompt</label>
                    <span className="text-xs text-gray-500">Source: {promptSource || 'Loading...'}</span>
                  </div>
                  {promptNotice && (
                    <div className={`mb-3 border rounded-lg p-3 ${promptIsNewTemplate ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                      <p className={`text-sm ${promptIsNewTemplate ? 'text-amber-900' : 'text-blue-800'}`}>
                        {promptNotice}
                      </p>
                    </div>
                  )}
                  <textarea
                    value={promptValue}
                    onChange={(e) => {
                      setPromptValue(e.target.value);
                      if (promptIsNewTemplate && e.target.value.trim()) {
                        setPromptNotice('New template draft ready. Save Prompt will create the GitHub current/version files and update Retell.');
                      }
                    }}
                    rows={18}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm"
                    placeholder="Paste the Retell agent prompt here..."
                  />
                  <div className="flex items-center gap-4 mt-4">
                    <button
                      onClick={handleSavePrompt}
                      disabled={saving || !promptValue}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
                    >
                      {saving ? 'Saving...' : '💾 Save Prompt'}
                    </button>
                    <button
                      onClick={loadPrompt}
                      disabled={loadingPrompt}
                      className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-50"
                    >
                      {loadingPrompt ? '⏳ Loading...' : '📥 Load Current'}
                    </button>
                    <button
                      onClick={insertStarterPrompt}
                      className="px-4 py-2 bg-amber-100 rounded-lg hover:bg-amber-200 text-sm"
                    >
                      Insert Starter
                    </button>
                    <label className="px-4 py-2 bg-green-100 rounded-lg hover:bg-green-200 text-sm cursor-pointer">
                      📂 Load from File
                      <input
                        type="file"
                        accept=".md,.txt"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setPromptValue(ev.target.result);
                            setPromptSource(`Local file: ${file.name}`);
                            setPromptNotice('Loaded a local file. Save Prompt will create/update the GitHub current template, add a version file, save DoorBase, and push it to Retell.');
                            setPromptIsNewTemplate(true);
                            alert(`✅ Loaded: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
                          };
                          reader.readAsText(file);
                          e.target.value = ''; // reset so same file can be re-selected
                        }}
                      />
                    </label>
                    {saved && (
                      <span className="text-green-600 font-medium">✅ Saved!</span>
                    )}
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Load Current gets the GitHub current template for this agent. Save Prompt creates or updates GitHub current, writes a version file, saves DoorBase, and pushes the prompt to Retell.
                  </p>
                </div>
              </div>
            )}

            {/* WEBHOOK TAB */}
            {activeTab === 'webhook' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Retell Webhook URL</label>
                  <input
                    type="text"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="/api/retell/webhook"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono"
                  />
                  <div className="flex items-center gap-4 mt-4">
                    <button
                      onClick={handleSaveWebhook}
                      disabled={saving || !webhookUrl}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
                    >
                      {saving ? 'Saving...' : '💾 Save Webhook'}
                    </button>
                    {saved && <span className="text-green-600 font-medium">✅ Saved!</span>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Webhook Secret (Retell-Signature)</label>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={webhookSecret}
                      onChange={(e) => setWebhookSecret(e.target.value)}
                      placeholder="Paste secret from Retell Dashboard → Webhooks"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Get this from Retell Dashboard → Webhooks → Signing Secret. Once saved, signature verification is enforced.
                  </p>
                  <div className="flex items-center gap-4 mt-4">
                    <button
                      onClick={handleSaveWebhookSecret}
                      disabled={saving || !webhookSecret}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
                    >
                      {saving ? 'Saving...' : '💾 Save Secret'}
                    </button>
                    {saved && <span className="text-green-600 font-medium">✅ Saved!</span>}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    <strong>Current webhook:</strong> Retell calls this URL when events occur (call started, ended, etc.)
                    <br/><code className="bg-white px-2 py-1 rounded mt-2 inline-block">{webhookUrl || 'Not set'}</code>
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    <strong>Signature status:</strong>{' '}
                    {retellStatus?.has_webhook_secret || webhookSecret ? (
                      <span className="text-green-600 font-medium">🔒 Enforced</span>
                    ) : (
                      <span className="text-amber-600 font-medium">⚠️ Not configured — webhooks accepted without verification</span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* CALLS TAB */}
            {activeTab === 'calls' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium">Recent Calls</h3>
                  <button
                    onClick={() => loadCalls(selectedCompany)}
                    className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
                  >
                    🔄 Refresh
                  </button>
                </div>
                {calls.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No calls recorded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Call ID</th>
                          <th className="px-4 py-2 text-left">Direction</th>
                          <th className="px-4 py-2 text-left">From</th>
                          <th className="px-4 py-2 text-left">To</th>
                          <th className="px-4 py-2 text-left">Duration</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calls.map((call, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-4 py-2 font-mono text-xs">{call.call_id}</td>
                            <td className="px-4 py-2">{call.direction}</td>
                            <td className="px-4 py-2 font-mono text-xs">{call.from_number}</td>
                            <td className="px-4 py-2 font-mono text-xs">{call.to_number}</td>
                            <td className="px-4 py-2">{call.duration ? `${call.duration}s` : '-'}</td>
                            <td className="px-4 py-2">{call.call_status || '-'}</td>
                            <td className="px-4 py-2">{call.created_at ? new Date(call.created_at).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* VERSIONS TAB */}
            {activeTab === 'versions' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Prompt Version History</h3>
                {versions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No prompt versions saved yet.</p>
                ) : (
                  <div className="space-y-3">
                    {versions.map((v, i) => (
                      <div key={v.id || i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold bg-blue-500">
                          #{v.id || versions.length - i}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">Save #{v.id || versions.length - i}</p>
                          <p className="text-sm text-gray-600">Prompt version {v.version || 'n/a'}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Saved {v.created_at ? new Date(v.created_at).toLocaleString() : '-'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRestoreVersion(v.id)}
                          className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                        >
                          Restore
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </main>
    </div>
  );
}

export default SettingsRetellManagement;
